// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { action, computed, observable } from 'mobx';
import { toast } from 'shared/components/common/misc/Toast';
import { localize } from 'shared/components/other';
import { BaseStore } from 'shared/stores';
import { PivotMode, PivotStructure } from 'types/reportConstructor';

import { overridePivot } from './helpers/overridePivot';
import { ReportConstructorStore } from './ReportConstructor.store';
import { getNumberFormat } from './helpers';

type Deps = {
  reportConstructorStore: ReportConstructorStore;
};

const XLSX_SCRIPT_URL = '//cdn.webix.com/extras/xlsx.core.styles.min.js';

export class PivotStore extends BaseStore<Deps> {
  @observable
  structure: PivotStructure | null = null;

  @observable
  pivotMode: PivotMode | null = null;

  @observable
  private pivotView: typeof pivot.views.JetView | null = null;

  private eventId: number | null = null;

  @computed
  get isStructureEmpty(): boolean {
    return this.structure === null || (this.structure.rows.length === 0 && this.structure.columns.length === 0);
  }

  @action
  createPivot = (container: string | null, pivotMode: 'tree' | 'table' = 'tree'): void => {
    const { reportStructure, mode, selectedColumns } = this.deps.reportConstructorStore;

    if (mode !== 'new' && (reportStructure === null || selectedColumns.length === 0)) {
      toast.info(localize('report-constructor.errors.missing-data'));
      return;
    }

    webix.ready(() => {
      this.cleanUp();

      webix.CustomScroll.init();
      webix.i18n.setLocale('ru-RU');

      this.pivotView = webix.ui({
        container,
        view: 'pivot',
        id: 'pivot',
        mode: pivotMode,
        freezeColumns: false,
        override: overridePivot(this.deps.reportConstructorStore),
        fields: selectedColumns.map(({ label, value, valueType, usedInAggregation }) => ({
          id: value,
          value: label,
          type: valueType === 'STRING' ? 'text' : valueType === 'NUMERIC' ? 'number' : 'date',
          predicate: valueType === 'DATE' ? 'date' : valueType === 'NUMERIC' ? 'number' : undefined,
          usedInAggregation,
          usedInFilters: valueType === 'STRING',
        })),
        predicates: {
          date: webix.Date.dateToStr('%d/%m/%Y'),
          number: (value: unknown) => getNumberFormat(value),
        },
        readonly: mode === 'view',
        config: mode !== 'view',
        datatable: {
          footer: false,
          totalColumn: false,
          columnWidth: 150,
        },
        externalProcessing: true,
        ...(reportStructure ? { structure: reportStructure } : {}),
      });

      $$('pivot').getState().$observe('structure', this.setStructure);
      $$('pivot').getState().$observe('mode', this.setPivotMode);

      this.eventId = webix.event(window, 'resize', () => {
        this.pivotView?.adjust();
      });
    });
  };

  exportToExcel = async (filename: string): Promise<void> => {
    const data = await $$('pivot').getService('local').getData(false);

    if (!data.data?.length) {
      toast.info(localize('report-constructor.errors.missing-data'));
      return;
    }

    await this.ensureXlsxLoaded();
    const structure = $$('pivot').getStructure();
    const exportData = this.prepareDataForExport(data, structure);

    if (!exportData.rows.length) {
      toast.info(localize('report-constructor.errors.missing-data'));
      return;
    }

    const container = document.createElement('div');
    container.style.cssText = 'position:absolute;left:-9999px;top:-9999px;visibility:hidden;';
    document.body.appendChild(container);

    const tempTable = webix.ui({
      container,
      view: 'datatable',
      data: exportData.rows,
      columns: exportData.columns.map((col) => ({
        id: col.id,
        header: col.header,
        width: col.width,
        template: (obj: Record<string, unknown>) => obj[col.id] ?? '',
      })),
      header: true,
      footer: false,
    });

    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      await webix.toExcel(tempTable, {
        filename,
        styles: true,
        spans: false,
        rawValues: true,
        download: true,
      });
    } finally {
      tempTable.destructor();
      container.remove();
    }
  };

  private ensureXlsxLoaded = (): Promise<void> => {
    const isLoaded = typeof (window as unknown as { XLSX?: unknown }).XLSX !== 'undefined';
    if (isLoaded) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = XLSX_SCRIPT_URL;
      script.async = true;

      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load XLSX library'));

      setTimeout(() => reject(new Error('XLSX load timeout')), 5000);

      document.head.appendChild(script);
    });
  };

  private prepareDataForExport = (
    data: Record<string, unknown>,
    structure: PivotStructure,
  ): { rows: Record<string, unknown>[]; columns: { id: string; header: string; width: number }[] } => {
    const result = data.data as unknown[];

    const columns: { id: string; header: string; width: number }[] = [];

    // Колонки для строк
    structure.rows.forEach((rowField, index) => {
      columns.push({
        id: `row_${index}`,
        header: rowField,
        width: 150,
      });
    });

    // Колонки для значений
    structure.values.forEach((value, index) => {
      const valueName = Array.isArray(value.name) ? value.name[0] : value.name;
      columns.push({
        id: `value_${index}`,
        header: `${valueName} (${value.operation})`,
        width: 150,
      });
    });

    // Преобразуем данные (поддержка tree и table форматов)
    const rows: Record<string, unknown>[] = [];

    (result || []).forEach((item) => {
      if (Array.isArray(item)) {
        // Table format: массив значений
        const row: Record<string, unknown> = {};
        item.forEach((cell, index) => {
          if (columns[index]) {
            row[columns[index].id] = cell;
          }
        });
        rows.push(row);
      } else if (item && typeof item === 'object' && 'values' in item) {
        // Tree format: объект с полем values
        const treeItem = item as { values: unknown[] };
        const row: Record<string, unknown> = {};
        treeItem.values.forEach((cell, index) => {
          if (columns[index]) {
            row[columns[index].id] = cell;
          }
        });
        rows.push(row);
      }
    });

    return { rows, columns };
  };

  cleanUp = (): void => {
    this.structure = null;
    this.pivotMode = null;
    this.pivotView?.destructor();

    if (this.eventId) {
      webix.eventRemove(this.eventId);
      this.eventId = null;
    }
  };

  @action
  private setStructure = (structure: PivotStructure): void => {
    this.structure = structure;
  };

  @action
  private setPivotMode = (pivotMode: PivotMode): void => {
    this.pivotMode = pivotMode;
  };
}
